import React, { useEffect, useState } from "react";

import { FileDisplay } from "../../components/FileToDisplay/FileToDisplay.types";
import { CreateModal } from "../../components/Modals";
import { useNotify } from "../../context/NotificationContext";
import {
  fetchLessonById,
  fetchLessonCategories,
  updateLesson,
} from "../../services/lessonService/lessonService";
import type { LessonData } from "../../services/lessonService/lessonService.types";
import { formatServerErrors } from "../../utils/errorHandler";
import { getServerErrorData } from "../../utils/httpError";
import { logger } from "../../utils/logger";
import type { Lesson } from "../LessonsPage/LessonsPage.types";

interface KnowledgeBaseLessonEditModalProps {
  open: boolean;
  courseId: number;
  lessonId: number | null;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
}

const KnowledgeBaseLessonEditModal: React.FC<KnowledgeBaseLessonEditModalProps> = ({
  open,
  courseId,
  lessonId,
  onClose,
  onSaved,
}) => {
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [storageFiles, setStorageFiles] = useState<FileDisplay[]>([]);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const toast = useNotify();

  useEffect(() => {
    if (!open || !courseId || !lessonId) {
      setLesson(null);
      setStorageFiles([]);
      setExternalErrors({});
      return;
    }

    let active = true;
    Promise.all([
      fetchLessonById(courseId, lessonId),
      fetchLessonCategories(courseId),
    ])
      .then(([lessonData, categoryItems]) => {
        if (!active) return;
        setLesson(lessonData);
        setCategories(categoryItems);
        setStorageFiles(lessonData.files_from_storage || []);
        setExternalErrors({});
      })
      .catch((error) => {
        logger.error("Ошибка загрузки урока для редактирования:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось открыть редактирование урока",
          life: 3000,
        });
        onClose();
      });

    return () => {
      active = false;
    };
  }, [courseId, lessonId, onClose, open, toast]);

  if (!open || !lesson) return null;

  const handleSave = async (
    formData: Record<string, string | File[] | null | FileDisplay[]>,
  ) => {
    setSaving(true);
    setExternalErrors({});

    try {
      const uploadedFiles = Array.isArray(formData.files)
        ? (formData.files as File[])
        : [];
      const lessonData: LessonData = {
        course: courseId,
        order: Number(formData.order),
        title: String(formData.title || "").trim(),
        category_title: String(formData.category || "").trim(),
        content: String(formData.content || ""),
        video_url: String(formData.video_url || ""),
        files: uploadedFiles,
        files_from_storage: storageFiles,
      };

      await updateLesson(courseId, lesson.id, lessonData);
      await onSaved?.();
      onClose();
      toast.show({
        severity: "success",
        summary: "Готово",
        detail: "Урок обновлён",
        life: 2500,
      });
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        setExternalErrors(formatServerErrors(serverErrors));
      } else {
        logger.error("Ошибка сохранения урока Базы знаний:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось сохранить урок",
          life: 3000,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <CreateModal
      title="Редактировать урок"
      hasDragAndDrop={true}
      fields={[
        {
          name: "order",
          type: "number",
          placeholder: "Порядковый номер",
          required: true,
        },
        {
          name: "title",
          type: "text",
          placeholder: "Название",
          required: true,
        },
        {
          name: "category",
          type: "selectWithInput",
          placeholder: "Раздел",
          options: categories,
          required: true,
        },
        {
          name: "content",
          type: "textarea",
          placeholder: "Содержание",
        },
      ]}
      onSubmit={handleSave}
      submitButtonText="Сохранить"
      onCancel={onClose}
      onRemoveFile={(fileId) =>
        setStorageFiles((items) => items.filter((file) => file.id !== fileId))
      }
      initialValues={{
        order: lesson.order.toString(),
        title: lesson.title,
        category: lesson.category_title || "",
        content: lesson.content || "",
        video_url: lesson.video_url || "",
      }}
      files_from_storage={storageFiles}
      externalErrors={externalErrors}
      isLoading={saving}
    />
  );
};

export default KnowledgeBaseLessonEditModal;
